/**
 * RenameCommand
 *
 * Captures before/after name for a SceneObject rename.
 *
 * Commands hold a shared identity ref (via the scene's identity registry)
 * rather than a raw SceneObject pointer, so a rename — which mutates the
 * same instance in place — never invalidates any command in the stack.
 * The ref tracks the live instance across renames, Create/Delete undo
 * cycles, and any other identity-preserving mutation.
 */

import type { UndoCommand, SerializedCommand } from './UndoCommand';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import { registerCommandCodec } from './CommandCodec';

const CODEC_TYPE = 'Rename';

interface RenamePayload {
    /**
     * Path as it was BEFORE the rename was applied. Used to resolve the
     * target ref at replay time (replay's `redo()` runs against pre-rename
     * scene state, so the pre-rename path is the one that resolves).
     */
    preRenamePath: string;
    oldName: string;
    newName: string;
}

export class RenameCommand implements UndoCommand {
    readonly label: string;
    /** Shared identity ref — survives Create/Delete cycles and renames. */
    private identity: SceneObjectRef;
    private oldName: string;
    private newName: string;
    /**
     * Parent's path at construction time (empty string for top-level objects
     * parented to the scene root). Captured once so `serialize()` can
     * reconstruct the pre-rename FQTP as `<parentPath>/<oldName>` without
     * having to string-split the object's own path — which would break if
     * a name contains '/'.
     */
    private parentPathAtConstruction: string;

    constructor(sceneObject: SceneObject, oldName: string, newName: string) {
        this.identity = sceneObject.traceScene.objectIdentityRegistry.refFor(sceneObject);
        this.oldName = oldName;
        this.newName = newName;
        this.label = `Rename ${oldName} → ${newName}`;
        const parent = sceneObject.getParent();
        this.parentPathAtConstruction = parent ? parent.getPath() : '';
    }

    undo(): void {
        const obj = this.identity.get();
        if (!obj) return;
        const scene = obj.traceScene;
        if (!scene) return;
        scene.renameSceneObject(obj, this.oldName);
        scene.select(obj);
    }

    redo(): void {
        const obj = this.identity.get();
        if (!obj) return;
        const scene = obj.traceScene;
        if (!scene) return;
        scene.renameSceneObject(obj, this.newName);
        scene.select(obj);
    }

    serialize(): SerializedCommand {
        // Join parent path + old name to get the pre-rename FQTP. Empty
        // parent path = top-level object; `resolveSceneObject` accepts a
        // bare name for that case.
        const preRenamePath = this.parentPathAtConstruction
            ? `${this.parentPathAtConstruction}/${this.oldName}`
            : this.oldName;
        const payload: RenamePayload = {
            preRenamePath,
            oldName: this.oldName,
            newName: this.newName,
        };
        return { type: CODEC_TYPE, data: payload };
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): RenameCommand => {
    const p = data as RenamePayload;
    const obj = scene.resolveSceneObject(p.preRenamePath);
    if (!obj) {
        throw new Error(`[RenameCommand] Cannot resolve path "${p.preRenamePath}"`);
    }
    return new RenameCommand(obj, p.oldName, p.newName);
});
